import {
  TEMPLATE_VARIABLE_ATTR,
  TEMPLATE_VARIABLE_BLOCK_ATTR,
} from '../templateVariableAttrs.ts';
import { restoreImageRefs } from '../image/imageWireFormat.ts';

// Serializes the editor's HTML output back to the `{{key}}` template format
// the backend stores. Called on every editor `update` event, so this is the
// inverse of `convertTemplateToTiptap` and the two must stay roundtrip-safe.
export const convertTiptapToTemplate = (tiptapHtml: string): string => {
  const doc = new DOMParser().parseFromString(tiptapHtml, 'text/html');
  restoreVariableSyntax(doc);
  normalizeTableColumnWidths(doc);
  restoreImageRefs(doc);
  return doc.body.innerHTML;
};

// Column widths are persisted as PERCENTAGES of the table, never px. TipTap's
// model is px-native — it sizes columns against the editor canvas — but those px
// are meaningless on another surface: the PDF page is far narrower than the
// editor, so a px column width (and the px table width TipTap derives from it)
// either overflows the page or loses its intended proportion. A percentage is
// surface-independent: `width: 25%` is a quarter of the table in every sink.
//
// So on save we rewrite each `<col>`'s px width to a percentage of their sum and
// pin the table to `width: 100%` (overriding the px table width TipTap emits for
// a resized table). The editor reads these back as px against a fixed reference
// canvas on load (see `tableExtensions.recoverColwidth`), so the proportions
// round-trip exactly. The `colwidth` attribute TipTap also writes on cells is
// dropped here — it never survives the sanitizer and the width now lives on the
// `<col>` elements.
const normalizeTableColumnWidths = (doc: Document): void => {
  doc.querySelectorAll('table').forEach((table) => {
    table
      .querySelectorAll('td[colwidth], th[colwidth]')
      .forEach((cell) => cell.removeAttribute('colwidth'));

    const cols = Array.from(
      table.querySelectorAll<HTMLElement>('colgroup > col'),
    );
    const pxWidths = cols.map((col) =>
      col.style.width.endsWith('px') ? parseFloat(col.style.width) : NaN,
    );
    const total = pxWidths.reduce((sum, width) => sum + width, 0);

    // Only a fully column-sized table (every col has a px width) is converted.
    // A never-resized table carries `min-width` instead — leave it `width: 100%`
    // with evenly distributed columns. An already-`%` table has no `px` width
    // and is skipped too, so re-saving is idempotent.
    if (
      !cols.length ||
      pxWidths.some((w) => !Number.isFinite(w)) ||
      total <= 0
    ) {
      return;
    }

    let remaining = 100;
    cols.forEach((col, index) => {
      // The last column absorbs the rounding remainder so the columns sum to
      // exactly 100%.
      const percent =
        index === cols.length - 1
          ? remaining
          : Math.round((pxWidths[index] / total) * 10000) / 100;
      remaining = Math.round((remaining - percent) * 100) / 100;
      col.style.removeProperty('min-width');
      col.style.setProperty('width', `${percent}%`);
    });

    table.style.removeProperty('min-width');
    table.style.setProperty('width', '100%');
  });
};

// Both inline and block placeholders become bare `{{key}}` text. For blocks
// this means the text node sits between sibling block elements (e.g.
// `<p>Hi</p>{{x}}<p>Bye</p>`) — valid HTML, and the inverse converter can
// naively replace `{{key}}` with a block marker div without producing
// nested-block artefacts.
const restoreVariableSyntax = (doc: Document): void => {
  doc.body
    .querySelectorAll(
      `[${TEMPLATE_VARIABLE_ATTR}], [${TEMPLATE_VARIABLE_BLOCK_ATTR}]`,
    )
    .forEach((el) => {
      const key =
        el.getAttribute(TEMPLATE_VARIABLE_ATTR) ??
        el.getAttribute(TEMPLATE_VARIABLE_BLOCK_ATTR) ??
        '';
      el.replaceWith(doc.createTextNode(`{{${key}}}`));
    });
};
